<template>
  <div class="admin-module" v-loading="loading">
    <el-card class="admin-card" shadow="never">
      <div class="admin-head">
        <div class="admin-head-text">
          <h2 class="admin-title">{{ moduleTitle }}</h2>
          <p class="hint">
            模块编码：<code>{{ moduleCode }}</code>
            <span class="hint-sub"
              >对外路由：<strong>/{{ moduleCode }}</strong></span
            >
          </p>
        </div>
        <div class="toolbar">
          <div class="toolbar-upload-col">
            <el-upload
              ref="batchUpload"
              class="admin-upload"
              action="#"
              multiple
              :auto-upload="false"
              :show-file-list="false"
              accept="image/*"
              :disabled="uploadBusy"
              :on-change="onBatchUploadChange"
            >
              <el-button
                type="primary"
                icon="el-icon-upload"
                size="medium"
                :loading="uploadBusy"
                :disabled="uploadBusy"
              >
                {{ uploadBusy ? "上传中…" : "上传新幻灯片" }}
              </el-button>
            </el-upload>
            <p class="upload-order-hint">
              单张图片不超过 <strong>{{ slideUploadMaxMb }} MB</strong>。
              一次可选多张；入库顺序按<strong>文件名自然排序</strong>（如
              1.jpg、2.jpg…10.jpg）。多张将依次上传，若有失败会跳过并继续，结束后汇总未成功的文件。
            </p>
            <p v-if="designSizeHint" class="upload-size-hint">
              建议单页尺寸：<strong>{{ designSizeHint }}</strong>
              <span class="upload-size-hint-sub">
                宽 × 高（像素），与本模块对外大屏设计稿一致
              </span>
            </p>
          </div>
          <el-button
            size="medium"
            plain
            icon="el-icon-refresh"
            :disabled="uploadBusy"
            @click="fetchList"
          >
            刷新
          </el-button>
          <el-button
            size="medium"
            plain
            type="danger"
            icon="el-icon-delete"
            :disabled="uploadBusy || slides.length === 0"
            @click="removeAllSlides"
          >
            全部删除
          </el-button>
        </div>
      </div>

      <el-table
        class="admin-table"
        :data="slides"
        border
        stripe
        style="width: 100%"
        :header-cell-style="tableHeaderStyle"
        empty-text="暂无幻灯片，请点击「上传新幻灯片」添加"
      >
        <el-table-column type="index" label="#" width="50" />
        <el-table-column label="图片" width="260" align="center">
          <template slot-scope="scope">
            <el-image
              class="admin-thumb-image"
              :src="previewUrl(scope.row)"
              :preview-src-list="previewSrcList"
              fit="contain"
              style="width: 140px; height: 80px"
            >
              <div slot="error" class="image-slot">加载失败</div>
            </el-image>
          </template>
        </el-table-column>
        <el-table-column
          prop="id"
          label="ID"
          min-width="200"
          show-overflow-tooltip
        />
        <el-table-column
          label="操作"
          width="400"
          fixed="right"
          align="center"
          header-align="center"
        >
          <template slot-scope="scope">
            <div class="admin-actions-row">
              <el-button
                size="mini"
                :disabled="scope.$index === 0"
                @click="moveUp(scope.$index)"
              >
                上移
              </el-button>
              <el-button
                size="mini"
                :disabled="scope.$index === slides.length - 1"
                @click="moveDown(scope.$index)"
              >
                下移
              </el-button>
              <el-upload
                class="admin-upload-inline"
                action="#"
                :show-file-list="false"
                accept="image/*"
                :http-request="opts => replaceRow(scope.row, opts)"
              >
                <el-button size="mini" type="warning">替换图片</el-button>
              </el-upload>
              <el-button
                size="mini"
                type="danger"
                @click="removeRow(scope.row)"
              >
                删除
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { Message, MessageBox } from "element-ui";
import {
  PPT_MODULES,
  PPT_MODULE_CODES,
  isValidModuleCode
} from "@/constants/pptModules";
import {
  fetchSlidesList,
  deleteSlide,
  reorderSlides,
  uploadSlide,
  replaceSlideImage,
  resolveSlideImageUrl
} from "@/api/pptSlides";

/** 须与 PptMap.Api `PptSlides:UploadMaxMb` 一致 */
const SLIDE_UPLOAD_MAX_MB = 20;

export default {
  name: "AdminModuleSlides",
  props: {
    moduleCode: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      slides: [],
      /** 多选后 change 会连续触发，合并为一批再排序上传 */
      uploadBatchDebounceTimer: null,
      /** 防抖等待期内仍为 true，防止连点（uploadBatchRunning 尚未置位） */
      uploadBatchPending: false,
      uploadBatchRunning: false
    };
  },
  computed: {
    moduleTitle() {
      if (!isValidModuleCode(this.moduleCode)) {
        return "未知模块";
      }
      return PPT_MODULES[this.moduleCode].title;
    },
    tableHeaderStyle() {
      return {
        background: "#f5f7fa",
        color: "#303133",
        fontWeight: "600"
      };
    },
    /** 来自 pptModules.js，供运营按设计稿裁图 */
    designSizeHint() {
      if (!isValidModuleCode(this.moduleCode)) {
        return "";
      }
      const { designWidth, designHeight } = PPT_MODULES[this.moduleCode];
      return `${designWidth} × ${designHeight}`;
    },
    /** 与表格顺序一致，供 el-image 大图预览左右切换 */
    previewSrcList() {
      return this.slides.map(s => resolveSlideImageUrl(s.imageUrl));
    },
    /** 批量上传进行中（含防抖等待），用于禁用按钮与 loading 态 */
    uploadBusy() {
      return this.uploadBatchPending || this.uploadBatchRunning;
    },
    /** 与后端 UploadMaxMb 一致，用于文案 */
    slideUploadMaxMb() {
      return SLIDE_UPLOAD_MAX_MB;
    }
  },
  watch: {
    moduleCode: {
      immediate: true,
      handler() {
        this.bootstrap();
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this.uploadBatchDebounceTimer);
    this.uploadBatchPending = false;
  },
  methods: {
    bootstrap() {
      if (!isValidModuleCode(this.moduleCode)) {
        Message.error("无效的模块");
        this.$router.replace(`/admin/${PPT_MODULE_CODES[0]}`);
        return;
      }
      this.fetchList();
    },
    previewUrl(row) {
      return resolveSlideImageUrl(row.imageUrl);
    },
    normalizeList(raw) {
      const list = Array.isArray(raw) ? raw : [];
      return [...list].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    },
    fetchList() {
      this.loading = true;
      return fetchSlidesList(this.moduleCode)
        .then(res => {
          this.slides = this.normalizeList(res);
        })
        .catch(err => {
          Message.error(this.errText(err, "加载列表失败"));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    errText(err, fallback) {
      if (err && err.response && err.response.data != null) {
        const d = err.response.data;
        if (typeof d === "string") {
          return d;
        }
        if (d.message) {
          return d.message;
        }
        if (d.Message) {
          return d.Message;
        }
      }
      if (err && err.message) {
        return err.message;
      }
      return fallback;
    },
    onBatchUploadChange(file, fileList) {
      // 必须在同一同步栈内从 fileList 取出 raw：防抖回调执行时 el-upload 往往已清空内部列表，
      // 延后再读会得到 []，表现为「选完文件没有任何反应」。
      // 多选时 Element 会对每个文件依次触发 on-change（如 43 张会触发 43 次），每次 fileList 递增；
      // 只能在「防抖窗口」内用最后一次快照合并为一批，绝不能在上传未开始时因 pending 直接 return，
      // 否则只会保留第一张，并会对后续每次触发弹「正在上传」造成刷屏。
      let rawFiles = (fileList || []).map(f => f.raw).filter(Boolean);
      if (!rawFiles.length && file && file.raw) {
        rawFiles = [file.raw];
      }
      if (!rawFiles.length) {
        return;
      }
      if (this.uploadBatchRunning) {
        Message.warning("正在上传，请稍候");
        return;
      }
      this.uploadBatchPending = true;
      clearTimeout(this.uploadBatchDebounceTimer);
      this.uploadBatchDebounceTimer = setTimeout(() => {
        this.uploadBatchDebounceTimer = null;
        this.uploadBatchPending = false;
        this.runSortedBatchUpload(rawFiles);
      }, 80);
    },
    /**
     * 按文件名自然排序后串行上传；失败跳过继续，最后汇总失败列表（策略 B）
     */
    async runSortedBatchUpload(rawFiles) {
      if (this.uploadBatchRunning) {
        return;
      }
      this.uploadBatchRunning = true;
      const failed = [];
      let ok = 0;
      try {
        const maxBytes = SLIDE_UPLOAD_MAX_MB * 1024 * 1024;
        const sorted = [...rawFiles].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base"
          })
        );
        const tooBig = sorted.filter(f => f.size > maxBytes);
        const toUpload = sorted.filter(f => f.size <= maxBytes);
        if (tooBig.length > 0) {
          Message.warning(
            `有 ${
              tooBig.length
            } 张超过 ${SLIDE_UPLOAD_MAX_MB} MB，已跳过：${tooBig
              .map(f => f.name)
              .join("、")}`
          );
        }
        if (toUpload.length === 0) {
          return;
        }
        this.loading = true;
        for (const file of toUpload) {
          try {
            await uploadSlide(this.moduleCode, file);
            ok++;
          } catch (err) {
            failed.push({
              name: file.name,
              message: this.errText(err, "上传失败")
            });
          }
        }
        await this.fetchList();
        if (ok > 0) {
          Message.success(`成功上传 ${ok} 张`);
        }
        if (failed.length > 0) {
          const text = failed.map(f => `「${f.name}」 ${f.message}`).join("\n");
          MessageBox.alert(text, `有 ${failed.length} 张未上传成功`, {
            type: "warning",
            confirmButtonText: "知道了",
            customClass: "admin-upload-fail-box"
          });
        }
      } finally {
        this.uploadBatchRunning = false;
        this.$nextTick(() => {
          if (this.$refs.batchUpload) {
            this.$refs.batchUpload.clearFiles();
          }
        });
      }
    },
    replaceRow(row, req) {
      const file = req.file;
      const maxBytes = SLIDE_UPLOAD_MAX_MB * 1024 * 1024;
      if (file.size > maxBytes) {
        Message.error(`文件超过 ${SLIDE_UPLOAD_MAX_MB} MB`);
        return;
      }
      this.loading = true;
      replaceSlideImage(this.moduleCode, row.id, file)
        .then(() => {
          Message.success("已替换");
          return this.fetchList();
        })
        .catch(err => {
          Message.error(this.errText(err, "替换失败"));
          this.loading = false;
        });
    },
    removeRow(row) {
      MessageBox.confirm("确定删除该张幻灯片？", "提示", {
        type: "warning"
      })
        .then(() => {
          this.loading = true;
          return deleteSlide(this.moduleCode, row.id);
        })
        .then(() => {
          Message.success("已删除");
          return this.fetchList();
        })
        .catch(err => {
          if (err !== "cancel") {
            Message.error(this.errText(err, "删除失败"));
            this.loading = false;
          }
        });
    },
    removeAllSlides() {
      const n = this.slides.length;
      if (n === 0) {
        return;
      }
      MessageBox.confirm(
        `确定删除本模块全部 ${n} 张幻灯片？此操作不可恢复。`,
        "全部删除",
        {
          type: "warning",
          confirmButtonText: "确定删除",
          cancelButtonText: "取消"
        }
      )
        .then(() => this.runRemoveAllSlides())
        .catch(() => {});
    },
    async runRemoveAllSlides() {
      const ids = this.slides.map(s => s.id);
      if (!ids.length) {
        return;
      }
      const failed = [];
      this.loading = true;
      try {
        for (const id of ids) {
          try {
            await deleteSlide(this.moduleCode, id);
          } catch (err) {
            failed.push({
              id,
              message: this.errText(err, "删除失败")
            });
          }
        }
        await this.fetchList();
        const ok = ids.length - failed.length;
        if (failed.length === 0) {
          Message.success("已全部删除");
        } else if (ok > 0) {
          Message.warning(`已删除 ${ok} 张，${failed.length} 张删除失败`);
          const text = failed.map(f => `「${f.id}」 ${f.message}`).join("\n");
          MessageBox.alert(text, "以下幻灯片删除失败", {
            type: "error",
            confirmButtonText: "知道了",
            customClass: "admin-upload-fail-box"
          });
        } else {
          Message.error("全部删除失败");
          const text = failed.map(f => `「${f.id}」 ${f.message}`).join("\n");
          MessageBox.alert(text, "删除失败", {
            type: "error",
            confirmButtonText: "知道了",
            customClass: "admin-upload-fail-box"
          });
        }
      } finally {
        this.loading = false;
      }
    },
    async moveUp(index) {
      if (index <= 0) {
        return;
      }
      const next = [...this.slides];
      const t = next[index - 1];
      next[index - 1] = next[index];
      next[index] = t;
      this.slides = next;
      await this.persistOrder();
    },
    async moveDown(index) {
      if (index >= this.slides.length - 1) {
        return;
      }
      const next = [...this.slides];
      const t = next[index + 1];
      next[index + 1] = next[index];
      next[index] = t;
      this.slides = next;
      await this.persistOrder();
    },
    persistOrder() {
      this.loading = true;
      const ids = this.slides.map(s => s.id);
      return reorderSlides(this.moduleCode, ids)
        .catch(err => {
          Message.error(this.errText(err, "保存顺序失败"));
          return this.fetchList();
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="less" scoped>
.admin-module {
  width: 100%;
  color: #303133;
}

.admin-card {
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  background: #fff;
}

.admin-card ::v-deep(.el-card__body) {
  padding: 20px 22px 24px;
}

.admin-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 18px;
  border-bottom: 1px solid #ebeef5;
}

.admin-head-text {
  flex: 1;
  min-width: 200px;
}

.admin-title {
  margin: 0 0 10px;
  font-size: 22px;
  font-weight: 600;
  color: #303133;
  line-height: 1.35;
}

.hint {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #606266;
}

.hint code {
  padding: 3px 8px;
  margin-right: 8px;
  font-size: 13px;
  color: #409eff;
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  border-radius: 4px;
}

.hint-sub {
  margin-left: 4px;
  color: #606266;
}

.hint-sub strong {
  color: #303133;
  font-weight: 600;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
}

.toolbar-upload-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  max-width: 420px;
}

.upload-size-hint {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #606266;
}

.upload-size-hint strong {
  color: #303133;
  font-weight: 600;
}

.upload-size-hint-sub {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.upload-order-hint {
  margin: 0;
  max-width: 520px;
  font-size: 12px;
  line-height: 1.55;
  color: #909399;
}

.upload-order-hint strong {
  color: #606266;
}

.admin-upload {
  display: inline-block;
}

.admin-upload ::v-deep(.el-upload) {
  display: inline-block;
}

.admin-table {
  font-size: 14px;
}

.admin-table ::v-deep(th),
.admin-table ::v-deep(td) {
  color: #303133 !important;
}

.admin-table ::v-deep(.cell) {
  color: #303133;
  line-height: 1.5;
}

.admin-table ::v-deep(.el-button--mini) {
  margin-right: 6px;
  margin-bottom: 4px;
}

.admin-actions-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.admin-actions-row ::v-deep(.el-button--mini) {
  margin-right: 0 !important;
  margin-bottom: 0 !important;
  flex-shrink: 0;
}

.admin-upload-inline {
  flex-shrink: 0;
}

.admin-upload-inline ::v-deep(.el-upload) {
  display: inline-flex;
  vertical-align: middle;
}

.admin-thumb-image {
  cursor: pointer;
  vertical-align: middle;
}

.image-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 12px;
  color: #909399;
  background: #f5f7fa;
  border-radius: 4px;
}
</style>

<style lang="less">
/* MessageBox 挂到 body，需非 scoped */
.admin-upload-fail-box {
  width: 520px;
  max-width: 92vw;
}

.admin-upload-fail-box .el-message-box__message {
  max-height: 50vh;
  overflow-y: auto;
  white-space: pre-wrap;
  text-align: left;
}
</style>
